import { createAsyncThunk, createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit"
import { AppDataState, AppId, AppThunkAPIType, unknownError } from "../../app/appTypes"
import { isSameRequestId } from "../../app/crud"
import { RootState } from "../../app/store"
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../http"
import { PackageName } from "../permissions/permissionsSlice"
import { logout, selectUserRole } from "../user/userSlice"

export type ModuleCode 
    = "admin" 
    | "validation" 
    | "urs"
    | "measure"
    | "offering"
    | "project"
    | "demo"
    | "marketplace"
    | "qms"
    | "super"

export type License = "BASIC" | "STANDARD" | "PREMIUM" | "CUSTOM" | "DEMO"

export interface Subscription {
    license: License
}

export type AnyModule = "*"

export const currentModuleStorageKey = "currentModuleCode"

const storedModuleCode = localStorage.getItem(currentModuleStorageKey) as ModuleCode | null

export interface Module {
    sysModuleId: AppId
    code: ModuleCode
    name: string
    description: string
    isActive: true
}

export interface ModuleActive { 
    moduleId: AppId
    code: ModuleCode
    name: string
    description: string
    active: boolean
    packages: CompanyPackage[]
}

export interface CompanyPackage {
    package: PackageName
    isActive: boolean
}

export interface Profile {
    subscription: { license: License } | null,
    modules: Module[]
}

export const activateModule = 
    createAsyncThunk<void, { companyId: AppId, moduleId: AppId }, AppThunkAPIType>("module/activate", async ({ moduleId, companyId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/sys/modules/activate/${companyId}/${moduleId}`,
            payload: {},
        }))
        if (!postWithAuth.fulfilled.match(result)) {
            const { payload } = result
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const deactivateModule = 
    createAsyncThunk<void, { companyId: AppId, moduleId: AppId }, AppThunkAPIType>("module/deactivate", async ({ moduleId, companyId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(deleteWithAuth({
            url: `api/sys/modules/disable/${companyId}/${moduleId}`,
        }))
        if (!deleteWithAuth.fulfilled.match(result)) {
            const { payload } = result
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const setCompanyPackageActive =
    createAsyncThunk<ModuleActive[], { companyId: AppId, package: PackageName, isActive: boolean }, AppThunkAPIType>("company_packages/set", async ({ companyId, package: pkg, isActive }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/sys/modules/company/${companyId}/packages/${pkg}`,
            payload: { isActive },
        }))
        const { payload } = result
        if (putWithAuth.fulfilled.match(result)) {
            return payload as ModuleActive[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadAllCompanyModules =
    createAsyncThunk<ModuleActive[], AppId, AppThunkAPIType>("company_modules/load", async (companyId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/sys/modules/company/${companyId}`
        }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as ModuleActive[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadUserModules =
    createAsyncThunk<Module[], AppId, AppThunkAPIType>("user_modules/load", async (companyId, { dispatch, rejectWithValue, getState }) => {
        const result = await dispatch(getWithAuth({
            url: `api/user-access/modules`,
        }))

        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            if (selectUserRole(getState()) === "super") {
                return [...payload as Module[], {
                    sysModuleId: "",
                    code: "super",
                    name: "Super",
                    description: "Super",
                    isActive: true,
                }]
            }
            return payload as Module[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadUserProfile =
    createAsyncThunk<Profile, void, AppThunkAPIType>("user_profile/load", async (_, { dispatch, rejectWithValue, getState }) => {
        const result = await dispatch(getWithAuth({
            url: `api/user-access/profile`,
        }))

        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            if (selectUserRole(getState()) === "super") {
                const profile = payload as Profile
                profile.modules.push({
                    sysModuleId: "",
                    code: "super",
                    name: "Super",
                    description: "Super",
                    isActive: true,
                })
                return profile
            }
            return payload as Profile
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadAllModules =
    createAsyncThunk<Module[], void, AppThunkAPIType>("modules/load", async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({
            url: `api/sys/modules`,
        }))

        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as Module[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

const moduleAdapter = createEntityAdapter<Module>({
    selectId: m => m.code,
})

export type ModuleState = EntityState<Module> & {
    state: AppDataState,
    currentModule: Module | null,
    preferredModuleCode: ModuleCode | null,
    license: License | null
}

const initialState: ModuleState = moduleAdapter.getInitialState({
    state: { type: "empty" },
    currentModule: null,
    preferredModuleCode: storedModuleCode,
    license: null,
})

export const moduleSlice = createSlice({
    name: "module",
    initialState,
    reducers: {
        setCurrentModule: (state, action: PayloadAction<Module>) => {
            if (state.state.type === "loaded" && state.entities[action.payload.code] !== undefined) {
                state.currentModule = action.payload
                state.preferredModuleCode = action.payload.code
            }
        },
    },
    extraReducers: builder => {
        builder.addCase(loadUserModules.pending, (state, action) => {
            if (state.state.type !== "loading") {
                state.state = {
                    type: "loading",
                    requestId: action.meta.requestId,
                }
            }
        })
        builder.addCase(loadUserProfile.pending, (state, action) => {
            if (state.state.type !== "loading") {
                state.state = {
                    type: "loading",
                    requestId: action.meta.requestId,
                }
            }
        })
        builder.addCase(loadUserModules.fulfilled, (state, action) => {
            if (isSameRequestId(state.state, action.meta.requestId)) {
                const modules = action.payload
                moduleAdapter.setAll(state, modules)
                state.state = { type: "loaded" }
                const preferredModule = modules.find(module => module.code === state.preferredModuleCode)
                state.currentModule = preferredModule ?? (modules.length === 1 ? modules[0] : null)
            }
        })
        builder.addCase(loadUserProfile.fulfilled, (state, action) => {
            if (isSameRequestId(state.state, action.meta.requestId)) {
                const profile = action.payload
                moduleAdapter.setAll(state, profile.modules)
                state.state = { type: "loaded" }
                state.license = profile.subscription?.license ?? null
                const preferredModule = profile.modules.find(module => module.code === state.preferredModuleCode)
                state.currentModule = preferredModule ?? (profile.modules.length === 1 ? profile.modules[0] : null)
            }
        })
        builder.addCase(loadUserModules.rejected, (state, action) => {
            if (isSameRequestId(state.state, action.meta.requestId)) {
                state.state = {
                    type: "error",
                    error: action.payload ?? unknownError(),
                }
            }
        })
        builder.addCase(loadUserProfile.rejected, (state, action) => {
            if (isSameRequestId(state.state, action.meta.requestId)) {
                state.state = {
                    type: "error",
                    error: action.payload ?? unknownError(),
                }
            }
        })
        builder.addCase(logout.fulfilled, (state, action) => {
            state.currentModule = null
            moduleAdapter.removeAll(state)
            state.state = { type: "empty" }
        })
    },
})

export const selectModule = (state: RootState): ModuleState => state.module

export const {
    selectAll: selectAllModules,
    selectById: selectModuleByCode,
} = moduleAdapter.getSelectors(selectModule)

export const { setCurrentModule } = moduleSlice.actions

export const isSuper = (state: RootState): boolean => state.module.currentModule?.code === 'super'

export default moduleSlice.reducer
